import {
  PreciseCallResult,
  PreciseCallResultAnswered,
  PreciseCallResultMissed,
  PreciseCallResultAbandoned,
  PreciseCallResultAbandonedAtPhoneTree,
  PreciseCallResultAbandonedAtCallQueue,
  PreciseCallResultAbandonedAtCallGroup,
  PreciseCallResultAbandonedAtDevice,
  PreciseCallResultAbandonedAtInfoMessage,
  PreciseCallResultAbandonedAtVoicemail,
  PreciseCallResultAbandonedAtAiReceptionist,
  PreciseCallResultForwardedConnected,
  PreciseCallResultForwardedNotConnected,
  PreciseCallResultForwardedAbandoned,
  PreciseCallResultSentToVoicemail,
  PreciseCallResultSentToInfoMessage,
  PreciseCallResultCallTerminated,
  PreciseCallResultSystemFailure,
  PreciseCallResultOutboundConnected,
  PreciseCallResultOutboundNotConnected,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { i18next } from '@frontend/i18n';
import {
  getAiOutcomeIconWithTooltip as _getAiOutcomeIconWithTooltip,
  getAiOutcomeIcon as _getAiOutcomeIcon,
  OUTCOME_MAPPING,
} from '@frontend/phone-ui';
import { ChipVariants } from '@frontend/design-system';

export const getAiOutcomeIcon = ({ outcome, useTooltip = false }: { outcome: AIOutcome; useTooltip?: boolean }) => {
  let _outcome: (typeof OUTCOME_MAPPING)[keyof typeof OUTCOME_MAPPING] | undefined;
  let tooltip = '';
  const getIcon = useTooltip ? _getAiOutcomeIconWithTooltip : _getAiOutcomeIcon;

  if (outcome === AIOutcome.AI_OUTCOME_END_CALL) {
    _outcome = OUTCOME_MAPPING.SUCCESS;
    tooltip = i18next.t('AI Receptionist successfully assisted the patient');
  } else if (outcome === AIOutcome.AI_OUTCOME_FATAL) {
    _outcome = OUTCOME_MAPPING.FATAL;
    tooltip = i18next.t('AI Receptionist failed to answer this call.');
  } else if (outcome === AIOutcome.AI_OUTCOME_ROUTE_MISSED) {
    _outcome = OUTCOME_MAPPING.ROUTE_MISSED;
    tooltip = i18next.t('Your office missed the transfer from the AI Receptionist. The caller may need assistance.');
  } else if (outcome === AIOutcome.AI_OUTCOME_ROUTE_TRANSFERRED) {
    _outcome = OUTCOME_MAPPING.ROUTE_ANSWERED;
    tooltip = i18next.t('AI Receptionist transferred the call to your office.');
  } else if (outcome === AIOutcome.AI_OUTCOME_TEXT_HANDOFF) {
    _outcome = OUTCOME_MAPPING.TEXT_HANDOFF;
    tooltip = i18next.t('AI Receptionist continued the conversation over text.');
  } else if (outcome === AIOutcome.AI_OUTCOME_INCOMPLETE) {
    _outcome = OUTCOME_MAPPING.INCOMPLETE;
    tooltip = i18next.t('Caller hung up before AI Receptionist could determine the outcome.');
  }

  return _outcome ? getIcon({ outcome: _outcome, tooltip }) : null;
};

export const getPreciseCallResultLabel = (result?: PreciseCallResult): string | undefined => {
  switch (result) {
    case PreciseCallResultAnswered:
      return i18next.t('Answered');
    case PreciseCallResultMissed:
      return i18next.t('Missed');
    case PreciseCallResultAbandoned:
    case PreciseCallResultAbandonedAtPhoneTree:
    case PreciseCallResultAbandonedAtCallQueue:
    case PreciseCallResultAbandonedAtCallGroup:
    case PreciseCallResultAbandonedAtDevice:
    case PreciseCallResultAbandonedAtInfoMessage:
    case PreciseCallResultAbandonedAtVoicemail:
    case PreciseCallResultAbandonedAtAiReceptionist:
      return i18next.t('Abandoned');
    case PreciseCallResultForwardedConnected:
      return i18next.t('Forwarded + Connected');
    case PreciseCallResultForwardedNotConnected:
      return i18next.t('Forwarded + Not Connected');
    case PreciseCallResultForwardedAbandoned:
      return i18next.t('Forwarded + Abandoned');
    case PreciseCallResultSentToVoicemail:
      return i18next.t('Sent to Voicemail');
    case PreciseCallResultSentToInfoMessage:
      return i18next.t('Sent to Play Message');
    case PreciseCallResultCallTerminated:
      return i18next.t('Call Terminated');
    case PreciseCallResultSystemFailure:
      return i18next.t('System Failure');
    case PreciseCallResultOutboundConnected:
      return i18next.t('Connected');
    case PreciseCallResultOutboundNotConnected:
      return i18next.t('Not Connected');
    default:
      return undefined;
  }
};

export const isVoicemailPreciseCallResult = (result?: PreciseCallResult): boolean => {
  switch (result) {
    case PreciseCallResultMissed:
    case PreciseCallResultSentToVoicemail:
      return true;
    default:
      return false;
  }
};

// Chip color per precise call result, mirroring the Result column styling.
export const getPreciseCallResultChipVariant = (result?: PreciseCallResult): ChipVariants => {
  switch (result) {
    case PreciseCallResultAnswered:
    case PreciseCallResultSentToVoicemail:
    case PreciseCallResultSentToInfoMessage:
    case PreciseCallResultForwardedConnected:
    case PreciseCallResultOutboundConnected:
      return 'primary';
    case PreciseCallResultMissed:
    case PreciseCallResultForwardedNotConnected:
    case PreciseCallResultSystemFailure:
    case PreciseCallResultCallTerminated:
      return 'critical';
    case PreciseCallResultAbandoned:
    case PreciseCallResultAbandonedAtPhoneTree:
    case PreciseCallResultAbandonedAtCallQueue:
    case PreciseCallResultAbandonedAtCallGroup:
    case PreciseCallResultAbandonedAtDevice:
    case PreciseCallResultAbandonedAtInfoMessage:
    case PreciseCallResultAbandonedAtVoicemail:
    case PreciseCallResultAbandonedAtAiReceptionist:
    case PreciseCallResultForwardedAbandoned:
    case PreciseCallResultOutboundNotConnected:
      return 'warn';
    default:
      return 'neutral';
  }
};

export const isAbandonedPreciseCallResult = (result?: PreciseCallResult): boolean => {
  switch (result) {
    case PreciseCallResultAbandoned:
    case PreciseCallResultAbandonedAtPhoneTree:
    case PreciseCallResultAbandonedAtCallQueue:
    case PreciseCallResultAbandonedAtCallGroup:
    case PreciseCallResultAbandonedAtDevice:
    case PreciseCallResultAbandonedAtInfoMessage:
    case PreciseCallResultAbandonedAtVoicemail:
    case PreciseCallResultAbandonedAtAiReceptionist:
      return true;
    default:
      return false;
  }
};

// Abandonment time is the elapsed time between the call starting and ending.
export const getAbandonmentDuration = (startedAt?: string, endedAt?: string): string | undefined => {
  if (!startedAt || !endedAt) return undefined;
  const durationMs = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  if (!Number.isFinite(durationMs) || durationMs < 0) return undefined;
  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return i18next.t('{{seconds}} sec', { seconds });
  return i18next.t('{{minutes}} min {{seconds}} sec', { minutes, seconds });
};
